import { useAuthContext } from "@auth/context/Auth.context";
import { useConfigurationContext } from "@hooks/useContext";
import Hotjar from "@hotjar/browser";
import { getStorageItem, setStorageItem } from "@shared/utils/localStorage";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DAILY_ACTIVE_USER_EVENT = "daily_active_user";
const DAILY_ACTIVE_USER_STORAGE_KEY = "sentinel-hotjar-daily-active-user";

const getCurrentDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${today.getFullYear()}-${month}-${day}`;
};

export const useHotjar = () => {
  const config = useConfigurationContext();
  const { userId } = useAuthContext();
  const { pathname } = useLocation();

  useEffect(() => {
    const siteId = Number(config?.SENTINEL_HOTJAR_ID);
    const hotjarVersion = Number(config?.SENTINEL_HOTJAR_VERSION);

    const hasInvalidConfiguration =
      !Number.isInteger(siteId) ||
      siteId <= 0 ||
      !Number.isInteger(hotjarVersion) ||
      hotjarVersion <= 0;

    if (hasInvalidConfiguration) {
      return;
    }

    if (!Hotjar.isReady()) {
      const initialized = Hotjar.init(siteId, hotjarVersion, {
        debug: true,
      });

      if (!initialized) {
        return;
      }
    }

    if (pathname !== "/" || !userId) {
      return;
    }

    const today = getCurrentDate();
    const storageKey = `${DAILY_ACTIVE_USER_STORAGE_KEY}:${userId}`;
    const lastSentDate = getStorageItem<string | null>(storageKey, null);

    if (lastSentDate === today) {
      return;
    }

    if (Hotjar.event(DAILY_ACTIVE_USER_EVENT)) {
      setStorageItem(storageKey, today);
    }
  }, [config, pathname, userId]);
};
